import axios from 'axios';
import { toast } from 'react-toastify';
import store from '../../../app/store/store';
import { incrementLoading, decrementLoading } from '../../../app/store/slices/loadingSlice';

//const API_URL = 'http://localhost:5199/api/';
const API_URL = 'https://dev-billeur-api-webapp-f3cnbwfybmhdbjap.westeurope-01.azurewebsites.net/api/';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // ✅ Always send HTTP-only cookies
  timeout: 300000,
});

// Function to handle logout
const handleLogout = () => {
  
  localStorage.removeItem('user');
  toast.warning('Session expired. Please login again.');
  
  setTimeout(() => {
    window.location.href = '/login';
  }, 1000);
};

axiosInstance.interceptors.request.use(
  (config) => {
    // ✅ Increment global loading counter
    store.dispatch(incrementLoading());
    
    // ✅ Remove Content-Type header for FormData to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    // ✅ No manual token handling - HTTP-only cookies are sent automatically
    return config;
  },
  (error) => {
    // ✅ Decrement loading counter on error
    store.dispatch(decrementLoading());
    toast.error('Request failed to send');
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // ✅ Decrement global loading counter on success
    store.dispatch(decrementLoading());
    
    // ✅ Check for backend error format with errorCode > 0
    const data = response.data;
    if (data && typeof data.errorCode === 'number' && data.errorCode > 0) {
      // Backend returned an error
      const errorMessage = data.message || 'An error occurred';
      
      // Only show toast if not suppressed by the request config
      const suppressToast = response.config._suppressToast || response.config.suppressErrorToast;
      if (!suppressToast) {
        toast.error(errorMessage);
      }
      
      // Reject the promise so catch blocks handle it
      return Promise.reject({
        isBackendError: true,
        errorCode: data.errorCode,
        message: errorMessage,
        status: data.status,
        statusCode: data.statusCode,
        response: { data }
      });
    }
    
    return response;
  },
  (error) => {
    // ✅ Decrement global loading counter on error
    store.dispatch(decrementLoading());
    
    // Check if toast should be suppressed for this request
    const suppressToast = error.response?.config?._suppressToast || 
                         error.response?.config?.suppressErrorToast ||
                         error.config?._suppressToast || 
                         error.config?.suppressErrorToast;
    
    if (error.response) {
      const { status, data, config } = error.response;
      
      // ✅ Check for backend error format with errorCode > 0
      if (data && typeof data.errorCode === 'number' && data.errorCode > 0) {
        const errorMessage = data.message || 'An error occurred';
        if (!suppressToast) {
          toast.error(errorMessage);
        }
        return Promise.reject({
          isBackendError: true,
          errorCode: data.errorCode,
          message: errorMessage,
          status: data.status,
          statusCode: data.statusCode,
          response: error.response
        });
      }
      
      // Check if this is a logout request
      const isLogoutRequest = config.url && config.url.includes('Account/logout');
      
      switch (status) {
        case 401:
          handleLogout();
          break;
          
        case 403:
          if (!isLogoutRequest && !suppressToast) {
            toast.error(data.message || 'Access denied to this resource');
          }
          break;
          
        case 404:
          // Don't show error toast for logout requests
          if (!isLogoutRequest && !suppressToast) {
            toast.error(data.message || 'Resource not found');
          }
          break;
          
        case 500:
          // No toast — network/connection errors are already caught by the error.request branch
          // and the offline notification bar handles internet disconnectivity
          break;
          
        default:
          if (!isLogoutRequest && !suppressToast) {
            toast.error(data.message || `Error: ${status}`);
          }
      }
    } else if (error.request) {
      if (!suppressToast && !toast.isActive('network-error')) {
        toast.error('Something went wrong. Please try again later.', { toastId: 'network-error' });
      }
    } else {
      if (!suppressToast && !toast.isActive('request-error')) {
        toast.error('An unexpected error occurred.', { toastId: 'request-error' });
      }
    }
    
    return Promise.reject(error);
  }
);

export default axiosInstance;